import { v2 } from "oc-threejs/short3";
import { Vector2 } from "three";
import { minMaxBox2 } from "../commons/Utils";
import * as ClosedInterval from "oc-commons/ClosedInterval";
import { Interval } from "oc-commons/ClosedInterval";
import { MAuswahlwert, MElement } from "../../metamodell";
import { PElement } from "../../metamodell_paths";
import _ from "lodash";
import {
  flügelbreiteZuAussenbreite,
  isDoppeltorFn,
  nullEpsilon,
  sizeExactEpsilon,
} from "./elementFunctions";
import { filterPathIntersectionCached } from "oc-app/metadata/GraphMetadata";

/**
 * sizeScore gibt 0 bei passend und sonst einen negativen Wert in der Größenordnung der Differenz
 * zurück. Falls es nicht anders geht, lieber zu klein als zu groß. Die Breite ist wichtiger als die Höhe.
 * Wenn zwei variable Elemente passen, dann wird das kleinere bevorzugt. Wenn ein Standard-Element und ein variables
 * Element passen, dann wird das Standard-Element bevorzugt.
 */
export const sizeScore = (
  elementMeta: MElement,
  wunschBreiteAussenabstandUndHöhe: Vector2,
  sollZwischenabstand: number
): number => {
  const mass = elementMeta.Mass;
  const zuBreite = (x) => flügelbreiteZuAussenbreite(isDoppeltorFn(elementMeta), x);
  // Schwellwert muss kleiner sein als der Abstand zwischen zwei 'fix' Maßen innerhalb der selben Serie.
  // Sonst wird beim Füllungswechsel automatisch auf das kleinere Maß gewechselt (bspw. aus 180x180 wird dann 90x180)
  const wunschbreiteÜberschrittenStrafe = 0.5;
  const höhenZuBreitenGewichtung = 0.1;
  // masselementMalus sollte gleich dem Epsilon zur Separierung Standard- von Maßgröße sein
  const masselementMalus = sizeExactEpsilon;
  // imMaßbereichAberZuKleinStrafeFaktor muss kleiner als masselementMalus sein
  const imMaßbereichAberZuKleinStrafeFaktor = masselementMalus / 2;
  // variable Breite prüfen
  let distanzVonWunschbreiteVariabel = Number.POSITIVE_INFINITY;
  if (mass.MinMaxX?.x < mass.MinMaxX?.y) {
    const wunschBreiteMax = wunschbreiteMinusZwischenabstandWennKleiner(
      wunschBreiteAussenabstandUndHöhe.x,
      sollZwischenabstand,
      zuBreite(mass.MinMaxX.y)
    );
    distanzVonWunschbreiteVariabel = getPunishedDistanzFromInterval(
      [mass.MinMaxX.x, mass.MinMaxX.y].map(zuBreite),
      wunschBreiteMax,
      wunschbreiteÜberschrittenStrafe,
      0,
      imMaßbereichAberZuKleinStrafeFaktor
    );
  }

  // Standard-Breite prüfen
  const wunschBreiteStandard = wunschbreiteMinusZwischenabstandWennKleiner(
    wunschBreiteAussenabstandUndHöhe.x,
    sollZwischenabstand,
    zuBreite(mass.Standard.x)
  );
  const distanzVonWunschbreiteStandard = getPunishedDistanz(
    zuBreite(mass.Standard.x),
    wunschBreiteStandard,
    wunschbreiteÜberschrittenStrafe,
    0
  );

  // die Distanz ist jeweils der kleinere Wert
  // bei ansonstem Gleichstand soll die Standardgröße immer einen kleinen Vorteil haben
  // Im Prinzip ist die Score-Funktion ausserhalb des minMax - Bereiches linear ansteigend,
  // innerhalb der minMax-Bereiches konstant flach, aber mit einer winzigen Einkerbung +- 1 Millimeter
  // um die Standardgröße herum
  const distanzVonWunschbreite = Math.min(
    distanzVonWunschbreiteVariabel + masselementMalus,
    distanzVonWunschbreiteStandard
  );

  // variable Höhe prüfen
  let distanzVonWunschhöheVariabel = Number.POSITIVE_INFINITY;
  if (mass.MinMaxX?.x < mass.MinMaxX?.y) {
    distanzVonWunschhöheVariabel = getPunishedDistanzFromInterval(
      [mass.MinMaxY.x, mass.MinMaxY.y],
      wunschBreiteAussenabstandUndHöhe.y,
      0,
      0,
      imMaßbereichAberZuKleinStrafeFaktor
    );
  }

  // Standard-Höhe prüfen
  const distanzVonWunschhöheStandard = getPunishedDistanz(
    mass.Standard.y,
    wunschBreiteAussenabstandUndHöhe.y,
    0,
    0
  );

  // analog zur Wunschbreite
  const distanzVonWunschhöhe = Math.min(
    distanzVonWunschhöheVariabel + masselementMalus,
    distanzVonWunschhöheStandard
  );

  // die Höhe ist nicht so wichtig wie die exakte Breite
  // scoring ist absteigend sortiert, daher negativ - je weniger distanz, desto besser
  return -(distanzVonWunschbreite + höhenZuBreitenGewichtung * distanzVonWunschhöhe);
};

export const getHighestScoredMeta = (
  elements: MElement[],
  scoring: (element: MElement) => number
) => {
  const sorted = elements
    .map((element) => {
      const score = scoring(element);
      if (score == null || Number.isNaN(score)) {
        debugger;
        const score = scoring(element);
        throw new Error("score must be a number");
      }
      return {
        meta: element,
        score,
        Key: element.Key,
      };
    })
    .sort((a, b) => b.score - a.score);
  // console.log({ sorted });
  return _.first(sorted)?.meta;
};

export const getTargetSize = (
  element: MElement,
  nimmStandardBreite: boolean,
  sollZwischenabstand: number,
  wunschBreiteAussenabstandUndHöhe: Vector2
): Vector2 => {
  const zuBreite = (x) => flügelbreiteZuAussenbreite(isDoppeltorFn(element), x);
  const mass = element.Mass;
  const fix = mass.Variabel == "fix";
  if (fix) {
    return v2(zuBreite(mass.Standard.x), mass.Standard.y);
  }
  const zwischenabstand = nimmStandardBreite ? 0 : sollZwischenabstand;
  const targetSize = v2(
    wunschBreiteAussenabstandUndHöhe.x - zwischenabstand,
    wunschBreiteAussenabstandUndHöhe.y
  );
  const passtGenau = mass.Standard.distanceTo(targetSize) < sizeExactEpsilon;
  if (passtGenau) {
    return v2(zuBreite(mass.Standard.x), mass.Standard.y);
  }
  const box = minMaxBox2(mass);
  box.min.x = zuBreite(box.min.x);
  box.max.x = zuBreite(box.max.x);
  return box.clampPoint(targetSize, v2());
};

/**
 * prüft unter Einbeziehung der History, ob ein element eine zu den Auswahlwerten passende Füllung hat.
 * 0, falls ja, sonst ein negativer Wert von mindestens -1, der umso negativer wird, je weiter die Füllung
 * in der History zurück liegt, bis zu einem Minimum von -100
 */
export function matchFüllungWithHistory(
  element: MElement,
  füllungAuswahlwert: MAuswahlwert,
  füllungAuswahlwertHistory: MAuswahlwert[]
): { füllungAuswahlwert: MAuswahlwert; score: number } {
  const füllungenAuswahlwerte = filterPathIntersectionCached("elementScoring.ts:165")(
    element,
    (p: PElement) => p.Füllung.Auswahlwert
  ) as MAuswahlwert[];
  if (füllungAuswahlwert && füllungenAuswahlwerte.includes(füllungAuswahlwert)) {
    return { füllungAuswahlwert: füllungAuswahlwert, score: 0 };
  }
  const füllungsHistoryIndex = füllungAuswahlwertHistory.findIndex((füllung) =>
    füllungenAuswahlwerte.includes(füllung)
  );
  if (füllungsHistoryIndex == -1) {
    return { füllungAuswahlwert: null, score: -100 };
  }
  return {
    füllungAuswahlwert: füllungAuswahlwertHistory[füllungsHistoryIndex],
    score: (füllungAuswahlwert ? -1 : 0) - füllungsHistoryIndex,
  };
}

// "Aussenabstand" ist immer von Nachbar-Element-Kante|Segmentende|Mauer von Seite zu Seite, ohne Pfosten
export function getWunschBreiteAussenabstand(
  breite: number,
  nimmStandardBreite: boolean,
  istZwischenabstand: number
): number {
  return breite + (nimmStandardBreite ? 0 : istZwischenabstand);
}

export const wunschbreiteMinusZwischenabstandWennKleiner = (
  wunschBreiteAussenabstand: number,
  zwischenabstand: number,
  x: number
) => {
  // die wunschBreite berücksichtigt den Zwischenabstand nur, wenn das Element die gesamte Breite ausfüllen kann,
  // ansonsten bleibt sowieso ein Leerraum, und dann ist die unmodifizierte Standardbreite vorzuziehen
  const breitePasstVollständig = x + zwischenabstand < wunschBreiteAussenabstand;
  return wunschBreiteAussenabstand - (breitePasstVollständig ? 0 : zwischenabstand);
};

const getPunishedDistanz = (
  ist: number,
  wunsch: number,
  zuGroßStrafe: number,
  zuKleinStrafe: number
) => {
  const differenz = Math.abs(wunsch - ist);
  return differenz + (differenz < nullEpsilon ? 0 : ist < wunsch ? zuKleinStrafe : zuGroßStrafe);
};

const getPunishedDistanzFromInterval = (
  istIntervall: number[],
  wunsch: number,
  zuGroßStrafe: number,
  zuKleinStrafe: number,
  zuKleinInnenStrafeFaktor: number
) => {
  const differenz = ClosedInterval.distance(wunsch, istIntervall as Interval);
  if (differenz == 0) {
    if (istIntervall[1] - istIntervall[0] < nullEpsilon) {
      return 0;
    }
    return (
      ((istIntervall[1] - wunsch) / (istIntervall[1] - istIntervall[0])) * zuKleinInnenStrafeFaktor
    );
  }
  return differenz + (wunsch > istIntervall[0] ? zuGroßStrafe : zuKleinStrafe);
};
